'use client';
import React, { useState } from 'react';
import MaterialIcons from '../MaterialIcons/materialIcons';
import { Button } from '../ui/button';

type FooterLink = {
  label: string;
  _metadata: {
    uid: string;
    $: Record<string, unknown>;
  };
  link: {
    title: string;
    href: string;
    $: Record<string, unknown>;
  };
  open_in_new_tab: boolean;
  $: {
    label: {
      'data-cslp': string;
    };
    _metadata: {
      'data-cslp': string;
    };
    link: {
      'data-cslp': string;
    };
    open_in_new_tab: {
      'data-cslp': string;
    };
  };
};

type FooterColumn = {
  title: string;
  _metadata: Record<string, unknown>;
  items: FooterLink[];
  $: Record<string, unknown>;
};

type FooterData = {
  uid: string;
  _version: number;
  locale: string;
  ACL: {
    $: Record<string, unknown>;
  };
  _in_progress: boolean;
  bottom_links: {
    cta_title: string;
    cta_link: {
      title: string;
      href: string;
      $: Record<string, unknown>;
    };
    links: FooterLink[];
    copy: string;
    $: Record<string, unknown>;
  };
  columns: FooterColumn[];
  created_at: string;
  created_by: string;
  tags: string[];
  title: string;
  updated_at: string;
  updated_by: string;
  publish_details: {
    time: string;
    user: string;
    environment: string;
    locale: string;
    $: {
      time: Record<string, unknown>;
      user: Record<string, unknown>;
      environment: Record<string, unknown>;
      locale: Record<string, unknown>;
    };
  };
  $: {
    [key: string]: {
      'data-cslp'?: string;
      'data-cslp-parent-field'?: string;
    };
  };
};
// type
interface Props {
  footerData: FooterData;
}

const FooterClient = ({ footerData }: Props) => {
  const [openSection, setOpenSection] = useState<number | null>(null);
  const toggleSection = (idx: number) => {
    setOpenSection((prev) => (prev === idx ? null : idx));
  };

  const clickCookiePreferences = () => {
    const btn = document.getElementById('ot-sdk-btn-floating');
    const btnChild = btn?.querySelector('button');
    if (btnChild) {
      btnChild.click();
    }
  };

  return (
    <div
      className="cs-s-body relative z-[9] font-normal tab:section bg-surface-2 tab:pb-2 "
      data-test-id="footer-component"
    >
      <div
        className="mx-auto bg-surface-2 tab:bg-surface-2"
      >
        <footer
          className="mx-auto bg-surface-2 tab:bg-surface-2"
          data-swiftype-index="false"
        >
          {/* Mobile: Accordions, Desktop: Columns */}
          <div className="flex flex-col tab:mb-8 tab:hidden">
            {footerData?.columns?.map((section, idx) => (
              <div
                key={section?.title}
                className="text-text-default-strong"
              >
                <button
                  className="flex w-full items-center justify-between px-4 py-4 focus:outline-none"
                  onClick={() => toggleSection(idx)}
                  aria-expanded={openSection === idx}
                  aria-controls={`footer-section-${idx}`}
                >
                  <span className="text-base font-normal">
                    {section?.title}
                  </span>
                  <MaterialIcons
                    iconName="keyboard_arrow_down"
                    className={`h-5 w-5 transition-transform duration-300 ${
                      openSection === idx ? 'rotate-180' : 'rotate-0'
                    }`}
                  />
                </button>
                <ul
                  id={`footer-section-${idx}`}
                  className="overflow-hidden px-4 transition-all duration-300"
                  style={{
                    maxHeight: openSection === idx ? '1000px' : '0',
                    paddingTop: openSection === idx ? '1rem' : '0',
                    paddingBottom: openSection === idx ? '1rem' : '0',
                  }}
                >
                  {section.items.map((link, index) => (
                    <li key={index} data-test-id="footer-link">
                      <a href={link?.link.href}>
                        <div
                          className="mb-4 cursor-pointer text-sm text-text-default-light hover:text-text-default-strong transition-colors"
                        >
                          {link.label}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mb-20 hidden grid-cols-5 gap-8 tab:grid">
            {/* First 3 columns rendered normally */}
            {footerData?.columns?.slice(0, 3).map((section) => (
              <div key={section.title}>
                <h2
                  className="mb-4 text-lg font-inter text-text-default-strong"
                >
                  {section.title}
                </h2>
                <ul className="m-0 list-none p-0">
                  {section.items.map((item, index) => (
                    <li
                      key={index}
                      className="mb-3 text-sm text-text-default-light hover:text-text-default-strong transition-colors"
                      data-test-id="footer-link"
                    >
                      <a
                        href={item.link.href}
                        target={item.open_in_new_tab ? '_blank' : '_self'}
                        rel={
                          item.open_in_new_tab
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        className="cursor-pointer"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="col-span-1 flex flex-col gap-8">
              {footerData?.columns?.slice(3, 5).map((section) => (
                <div key={section?.title}>
                  <h2
                    className="mb-4 text-lg font-normal font-inter text-text-default-strong"
                  >
                    {section?.title}
                  </h2>
                  <ul className="m-0 list-none p-0">
                    {section?.items?.map((item, index) => (
                      <li
                        key={index}
                        className="mb-3 text-sm text-text-default-light hover:text-text-default-strong transition-colors"
                        data-test-id="footer-link"
                      >
                        <a
                          href={item?.link.href}
                          target={item?.open_in_new_tab ? '_blank' : '_self'}
                          rel={
                            item.open_in_new_tab
                              ? 'noopener noreferrer'
                              : undefined
                          }
                          className="cursor-pointer"
                        >
                          {item?.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="col-span-1 flex flex-col gap-8">
              {footerData?.columns?.slice(5, 7).map((section) => (
                <div key={section?.title}>
                  <h2
                    className="mb-4 text-lg font-normal font-inter text-text-default-strong"
                  >
                    {section?.title}
                  </h2>
                  <ul className="m-0 list-none p-0">
                    {section?.items.map((item, index) => (
                      <li
                        key={index}
                        className="mb-3 text-sm text-text-default-light hover:text-text-default-strong transition-colors"
                        data-test-id="footer-link"
                      >
                        <a
                          href={item.link.href}
                          target={item.open_in_new_tab ? '_blank' : '_self'}
                          rel={
                            item.open_in_new_tab
                              ? 'noopener noreferrer'
                              : undefined
                          }
                          className="cursor-pointer"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div
            className="flex flex-col gap-6 border-neutral-800 pt-4 pb-4 max-tab:pb-10 tab:flex-row tab:items-center tab:justify-between tab:gap-4 tab:border-t tab:border-border-light"
          >
            <div className="flex flex-col items-center gap-6 tab:flex-row">
              <a
                href={footerData?.bottom_links?.cta_link?.href}
                className="flex items-center cs-s-body bg-surface-3 text-text-default-strong"
                data-test-id="launch-button-link"
              >
                <Button
                  variant="SecondaryFillButton"
                  className="h-auto max-w-full py-3 text-center break-words whitespace-normal"
                >
                  <MaterialIcons
                    iconName="arrow_outward"
                    className="!text-[16px]"
                  />
                  {footerData?.bottom_links?.cta_title}
                </Button>
              </a>
              <div
                className="flex justify-center gap-4 text-sm text-text-default-light"
              >
                {footerData?.bottom_links?.links?.map((link, index) => (
                  <a
                    key={index}
                    className="cursor-pointer hover:text-text-default-strong"
                    href={link?.link?.href}
                    data-test-id="bottom-footer-link"
                  >
                    {link?.label}
                  </a>
                ))}
                <p
                  className="cursor-pointer hover:text-text-default-strong"
                  data-test-id="bottom-footer-link"
                  onClick={clickCookiePreferences}
                  data-swiftype-index="false"
                >
                  Cookies Settings
                </p>
              </div>
            </div>
            <div
              className="text-center text-sm text-text-default-light"
            >
              Copyright © {new Date().getFullYear()} Contentstack Inc. All
              rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FooterClient;
